// useRecipeTags — user-managed taxonomy for the /food/recipes surface.
// Mirrors useTodoTags but adds a category dimension (effort / cuisine /
// dish) so the Manage sheet can section the list and the LLM classifier
// can label each group distinctly.
//
// The server auto-seeds the legacy 24 tags on first load for any user
// who has never managed recipe tags, so this hook can be mounted
// unconditionally and the chip row never goes blank.

import { useCallback, useEffect, useMemo, useState } from "preact/hooks";
import { api, type RecipeTag, type RecipeTagCategory } from "./rpc";

export interface UseRecipeTags {
  tags: RecipeTag[];
  /** Same list bucketed by category. Empty arrays when the user has
   *  no tags in a category — UI hides empty sections. */
  byCategory: Record<RecipeTagCategory, RecipeTag[]>;
  loading: boolean;
  error: string | null;
  create: (name: string, category: RecipeTagCategory) => Promise<RecipeTag | null>;
  rename: (tagId: number, name: string) => Promise<RecipeTag | null>;
  setCategory: (tagId: number, category: RecipeTagCategory) => Promise<RecipeTag | null>;
  remove: (tagId: number) => Promise<number>;
  reorder: (category: RecipeTagCategory, ids: number[]) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useRecipeTags(): UseRecipeTags {
  const [tags, setTags] = useState<RecipeTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const next = await api.recipesTagsList();
      setTags(next);
      setError(null);
    } catch (err) {
      setError((err as Error)?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const byCategory = useMemo<Record<RecipeTagCategory, RecipeTag[]>>(() => {
    const m: Record<RecipeTagCategory, RecipeTag[]> = { effort: [], cuisine: [], dish: [] };
    for (const t of tags) {
      if (t.category === "effort" || t.category === "cuisine" || t.category === "dish") {
        m[t.category].push(t);
      }
    }
    for (const cat of Object.keys(m) as RecipeTagCategory[]) {
      m[cat].sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return m;
  }, [tags]);

  const create = useCallback(async (name: string, category: RecipeTagCategory): Promise<RecipeTag | null> => {
    try {
      const tag = await api.recipesTagCreate(name, category);
      setTags((prev) => [...prev, tag]);
      return tag;
    } catch (err) {
      setError((err as Error)?.message || String(err));
      return null;
    }
  }, []);

  const rename = useCallback(async (tagId: number, name: string): Promise<RecipeTag | null> => {
    try {
      const tag = await api.recipesTagRename(tagId, name);
      setTags((prev) => prev.map((t) => (t.id === tagId ? tag : t)));
      return tag;
    } catch (err) {
      setError((err as Error)?.message || String(err));
      await refresh();
      return null;
    }
  }, [refresh]);

  const setCategory = useCallback(async (tagId: number, category: RecipeTagCategory): Promise<RecipeTag | null> => {
    try {
      const tag = await api.recipesTagSetCategory(tagId, category);
      setTags((prev) => prev.map((t) => (t.id === tagId ? tag : t)));
      return tag;
    } catch (err) {
      setError((err as Error)?.message || String(err));
      await refresh();
      return null;
    }
  }, [refresh]);

  const remove = useCallback(async (tagId: number): Promise<number> => {
    const before = tags;
    setTags((prev) => prev.filter((t) => t.id !== tagId));
    try {
      const r = await api.recipesTagDelete(tagId);
      return r.affectedRecipes;
    } catch (err) {
      setError((err as Error)?.message || String(err));
      setTags(before);
      return 0;
    }
  }, [tags]);

  const reorder = useCallback(async (category: RecipeTagCategory, ids: number[]): Promise<void> => {
    setTags((prev) => {
      const byId = new Map(prev.map((t) => [t.id, t]));
      // Apply new sort_order for the affected category; keep other
      // categories untouched.
      ids.forEach((id, i) => {
        const cur = byId.get(id);
        if (cur && cur.category === category) byId.set(id, { ...cur, sortOrder: i });
      });
      return Array.from(byId.values());
    });
    try {
      const fresh = await api.recipesTagReorder(category, ids);
      setTags(fresh);
    } catch (err) {
      setError((err as Error)?.message || String(err));
      await refresh();
    }
  }, [refresh]);

  return { tags, byCategory, loading, error, create, rename, setCategory, remove, reorder, refresh };
}
